import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { hqApi } from "@/hq/hqApi";
import { Copy, ExternalLink, RefreshCw, Send, X } from "lucide-react";

const STATUS_STYLES = {
  DRAFT: "bg-[#EAEAEA] text-black",
  READY: "bg-[#FFE7CD] text-[#8A4A00]",
  SENT: "bg-[#FFE7CD] text-[#8A4A00]",
  PARTIALLY_PAID: "bg-[#FFF3D0] text-[#7A5A00]",
  PAID: "bg-[#D6F5D6] text-[#0E5A17]",
  OVERDUE: "bg-[#FFD3D3] text-[#8A0F0F]",
  CANCELLED: "bg-[#EAEAEA] text-[#8A8A8A]",
};

const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const fmt = (minor, cur) => `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusPill({ status }) {
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.18em] uppercase ${STATUS_STYLES[status] || "bg-[#EAEAEA] text-black"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

function AggregatesRow({ ag }) {
  if (!ag) return null;
  const currencies = Array.from(new Set([
    ...Object.keys(ag.outstanding_by_currency || {}),
    ...Object.keys(ag.overdue_by_currency || {}),
    ...Object.keys(ag.collected_this_month_by_currency || {}),
  ]));
  const tiles = [
    { key: "outstanding", label: "Outstanding", src: ag.outstanding_by_currency, tone: "text-[#8A4A00]" },
    { key: "overdue", label: "Overdue", src: ag.overdue_by_currency, tone: "text-[#8A0F0F]" },
    { key: "collected", label: "Collected this month", src: ag.collected_this_month_by_currency, tone: "text-[#0E5A17]" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
      {tiles.map((t) => (
        <div key={t.key} className="border-2 border-black rounded-2xl p-5 bg-white" data-testid={`hq-invoices-agg-${t.key}`}>
          <div className="text-[10px] font-bold tracking-[0.28em] uppercase text-black/50">{t.label}</div>
          <div className="mt-2 space-y-1">
            {currencies.length === 0 && <div className="text-[15px] font-black">—</div>}
            {currencies.map((c) => {
              const row = (t.src || {})[c];
              if (!row) return null;
              return (
                <div key={c} className="flex items-baseline justify-between">
                  <span className={`font-black text-2xl ${t.tone}`}>{fmt(row.amount_minor, c)}</span>
                  <span className="text-[11px] tracking-[0.16em] uppercase text-black/50">{row.count} inv</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function HQInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [aggregates, setAggregates] = useState(null);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [list, agg] = await Promise.all([
        hqApi.get("/api/hq/invoices", { params: filter ? { status: filter } : {} }),
        hqApi.get("/api/hq/invoices/dashboard/aggregates"),
      ]);
      setInvoices(list.data.items || []);
      setAggregates(agg.data);
    } catch (e) {
      toast.error("Failed to load invoices");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((i) =>
      (i.invoice_number || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.business_id || "").toLowerCase().includes(q)
    );
  }, [invoices, search]);

  const statuses = ["", "DRAFT", "READY", "SENT", "PARTIALLY_PAID", "PAID", "OVERDUE", "CANCELLED"];

  return (
    <div className="max-w-6xl mx-auto" data-testid="hq-invoices-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-[-0.02em]">Invoices & Payments</h1>
          <p className="text-sm text-black/60 mt-1">Custom project payments — one-time flows. Separate from Orange Optimise MRR.</p>
        </div>
        <Link to="/hq/invoices/new" data-testid="hq-invoices-new-cta" className="px-5 py-3 rounded-full bg-black text-white font-black text-[12px] tracking-[0.2em] uppercase hover:bg-[#1A1A1A]">
          Create invoice
        </Link>
      </div>

      <AggregatesRow ag={aggregates} />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex flex-wrap gap-1">
          {statuses.map((s) => (
            <button key={s || "ALL"} onClick={() => setFilter(s)}
              data-testid={`hq-invoices-filter-${s || "all"}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.14em] uppercase border ${filter === s ? "bg-black text-white border-black" : "bg-white text-black/70 border-[#EAEAEA]"}`}>
              {(s || "ALL").replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search invoice #, customer or project"
          data-testid="hq-invoices-search"
          className="flex-1 min-w-[220px] px-4 py-2 border-2 border-[#EAEAEA] rounded-full text-[13px] focus:outline-none focus:border-black" />
      </div>

      {loading ? (
        <div className="text-black/50 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[#EAEAEA] rounded-2xl p-12 text-center text-black/60" data-testid="hq-invoices-empty">
          No invoices match.
        </div>
      ) : (
        <div className="border-2 border-black rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FFF9EC]">
              <tr className="text-left [&_th]:px-4 [&_th]:py-3 [&_th]:font-bold [&_th]:text-[10px] [&_th]:tracking-[0.2em] [&_th]:uppercase">
                <th>Invoice</th><th>Description</th><th>Total</th><th>Paid</th><th>Outstanding</th><th>Status</th><th>Due</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6] cursor-pointer"
                    data-testid={`hq-invoice-row-${i.invoice_number}`}
                    onClick={() => navigate(`/hq/invoices/${i.id}`)}>
                  <td className="px-4 py-3 font-black">{i.invoice_number}</td>
                  <td className="px-4 py-3 text-black/80">{i.description || "—"}</td>
                  <td className="px-4 py-3 font-bold">{fmt(i.total_amount_minor, i.currency)}</td>
                  <td className="px-4 py-3">{fmt(i.amount_paid_minor, i.currency)}</td>
                  <td className="px-4 py-3">{fmt(i.amount_outstanding_minor, i.currency)}</td>
                  <td className="px-4 py-3"><StatusPill status={i.status} /></td>
                  <td className="px-4 py-3 text-black/60">{i.due_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- New invoice

const PRESETS = [
  { key: "full", label: "Pay in full", split: [1] },
  { key: "50_50", label: "50 / 50", split: [0.5, 0.5], labels: ["Deposit", "Final"] },
  { key: "40_30_30", label: "40 / 30 / 30", split: [0.4, 0.3, 0.3], labels: ["Deposit", "Interim", "Final"] },
];

export function HQInvoiceNew() {
  const navigate = useNavigate();
  const preParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const [form, setForm] = useState({
    business_id: preParams.get("business_id") || "",
    contact_id: preParams.get("contact_id") || "",
    project_id: preParams.get("project_id") || "",
    currency: "GBP",
    description: "", notes: "", issue_date: "", due_date: "",
    total_amount: "1000.00",
  });
  const [preset, setPreset] = useState("50_50");
  const [installments, setInstallments] = useState([
    { label: "Deposit", amount: "500.00", due_date: "", description: "" },
    { label: "Final", amount: "500.00", due_date: "", description: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const applyPreset = (key) => {
    setPreset(key);
    const total = Math.round(parseFloat(form.total_amount || "0") * 100);
    const p = PRESETS.find((x) => x.key === key);
    if (!p) return;
    if (key === "full") {
      setInstallments([{ label: "Full payment", amount: (total / 100).toFixed(2), due_date: form.due_date || "", description: "" }]);
    } else {
      // Preserve rounding by adjusting the last split so the sum equals total exactly.
      const minors = p.split.map((s) => Math.round(s * total));
      const drift = total - minors.reduce((a, b) => a + b, 0);
      minors[minors.length - 1] += drift;
      setInstallments(p.split.map((_, i) => ({
        label: (p.labels || [])[i] || `Payment ${i + 1}`,
        amount: (minors[i] / 100).toFixed(2),
        due_date: "", description: "",
      })));
    }
  };

  useEffect(() => { applyPreset(preset); /* eslint-disable-next-line */ }, [form.total_amount]);

  const totalMinor = Math.round(parseFloat(form.total_amount || "0") * 100);
  const scheduledMinor = installments.reduce((a, i) => a + Math.round(parseFloat(i.amount || "0") * 100), 0);
  const remaining = totalMinor - scheduledMinor;
  const canSubmit = totalMinor > 0 && remaining === 0 && installments.every((i) => i.label && Math.round(parseFloat(i.amount || "0") * 100) > 0);

  const provider = form.currency === "AED" ? "Stripe (Test)" : "PayPal (Sandbox)";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Schedule must sum to invoice total"); return; }
    setSubmitting(true);
    try {
      const payload = {
        business_id: form.business_id || null,
        contact_id: form.contact_id || null,
        project_id: form.project_id || null,
        currency: form.currency,
        total_amount_minor: totalMinor,
        description: form.description,
        notes: form.notes,
        issue_date: form.issue_date || null,
        due_date: form.due_date || null,
        installments: installments.map((i, idx) => ({
          label: i.label,
          amount_minor: Math.round(parseFloat(i.amount || "0") * 100),
          due_date: i.due_date || null,
          description: i.description || "",
        })),
      };
      const r = await hqApi.post("/api/hq/invoices", payload);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      navigate(`/hq/invoices/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create invoice");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-4xl mx-auto space-y-6" data-testid="hq-invoice-new-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-[-0.02em]">Create invoice</h1>
          <p className="text-sm text-black/60 mt-1">Build the schedule, review, save as draft or mark ready to send.</p>
        </div>
        <Link to="/hq/invoices" className="text-[11px] tracking-[0.2em] uppercase font-bold text-black/60 hover:text-black">← Back</Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-2 border-[#EAEAEA] rounded-2xl p-6 bg-white">
        {[
          ["business_id", "Customer / Business ID"],
          ["contact_id", "Contact ID (optional)"],
          ["project_id", "Project ID (optional)"],
          ["description", "Description / Service"],
          ["issue_date", "Issue date (YYYY-MM-DD)"],
          ["due_date", "Due date (YYYY-MM-DD)"],
        ].map(([k, label]) => (
          <label key={k} className="block">
            <span className="block text-[10px] tracking-[0.24em] uppercase font-bold text-black/50 mb-1">{label}</span>
            <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              data-testid={`hq-invoice-new-${k}`} className="w-full px-3 py-2.5 border-2 border-[#EAEAEA] rounded-xl text-[14px] focus:outline-none focus:border-black" />
          </label>
        ))}
        <label className="block">
          <span className="block text-[10px] tracking-[0.24em] uppercase font-bold text-black/50 mb-1">Currency</span>
          <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
            data-testid="hq-invoice-new-currency" className="w-full px-3 py-2.5 border-2 border-[#EAEAEA] rounded-xl text-[14px] focus:outline-none focus:border-black">
            {["GBP","USD","EUR","AED"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="mt-1 block text-[11px] text-black/50">Provider (backend-decided): <strong>{provider}</strong></span>
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.24em] uppercase font-bold text-black/50 mb-1">Invoice total ({form.currency})</span>
          <input value={form.total_amount} onChange={(e) => setForm((f) => ({ ...f, total_amount: e.target.value }))}
            data-testid="hq-invoice-new-total" inputMode="decimal" className="w-full px-3 py-2.5 border-2 border-[#EAEAEA] rounded-xl text-[14px] focus:outline-none focus:border-black" />
        </label>
        <label className="block md:col-span-2">
          <span className="block text-[10px] tracking-[0.24em] uppercase font-bold text-black/50 mb-1">Internal notes</span>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            data-testid="hq-invoice-new-notes" rows={2} className="w-full px-3 py-2.5 border-2 border-[#EAEAEA] rounded-xl text-[14px] focus:outline-none focus:border-black" />
        </label>
      </div>

      <div className="border-2 border-[#EAEAEA] rounded-2xl p-6 bg-white">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Payment schedule</div>
            <div className="mt-1 text-lg font-black">Choose a preset or build custom milestones</div>
          </div>
          <div className="flex gap-2">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => applyPreset(p.key)}
                data-testid={`hq-invoice-new-preset-${p.key}`}
                className={`px-3 py-2 rounded-full text-[11px] font-bold tracking-[0.14em] uppercase border-2 ${preset === p.key ? "bg-black text-white border-black" : "bg-white text-black border-[#EAEAEA]"}`}>
                {p.label}
              </button>
            ))}
            <button type="button" onClick={() => setInstallments([...installments, { label: `Payment ${installments.length + 1}`, amount: "0.00", due_date: "", description: "" }])}
              data-testid="hq-invoice-new-add-milestone" className="px-3 py-2 rounded-full text-[11px] font-bold tracking-[0.14em] uppercase border-2 border-[#EAEAEA] bg-white">
              + Custom
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {installments.map((inst, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`hq-invoice-new-milestone-${idx}`}>
              <input placeholder="Label" value={inst.label} onChange={(e) => setInstallments(installments.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} className="col-span-4 px-3 py-2 border-2 border-[#EAEAEA] rounded-xl text-[14px] focus:outline-none focus:border-black" />
              <input placeholder="0.00" value={inst.amount} onChange={(e) => setInstallments(installments.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} inputMode="decimal" className="col-span-3 px-3 py-2 border-2 border-[#EAEAEA] rounded-xl text-[14px] font-bold focus:outline-none focus:border-black" />
              <input placeholder="Due date" value={inst.due_date} onChange={(e) => setInstallments(installments.map((x, i) => i === idx ? { ...x, due_date: e.target.value } : x))} className="col-span-4 px-3 py-2 border-2 border-[#EAEAEA] rounded-xl text-[14px] focus:outline-none focus:border-black" />
              <button type="button" onClick={() => setInstallments(installments.filter((_, i) => i !== idx))} disabled={installments.length === 1} className="col-span-1 h-10 flex items-center justify-center rounded-xl border-2 border-[#EAEAEA] hover:border-black disabled:opacity-30">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-5 border-t border-[#EAEAEA] grid grid-cols-3 gap-2 text-center">
          <div><div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Invoice total</div><div className="mt-1 font-black text-xl">{fmt(totalMinor, form.currency)}</div></div>
          <div><div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Scheduled</div><div className="mt-1 font-black text-xl">{fmt(scheduledMinor, form.currency)}</div></div>
          <div><div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Remaining</div><div className={`mt-1 font-black text-xl ${remaining === 0 ? "text-[#0E5A17]" : "text-[#8A0F0F]"}`} data-testid="hq-invoice-new-remaining">{fmt(remaining, form.currency)}</div></div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Link to="/hq/invoices" className="px-6 py-3 rounded-full border-2 border-[#EAEAEA] font-black text-[12px] tracking-[0.2em] uppercase">Cancel</Link>
        <button type="submit" disabled={!canSubmit || submitting} data-testid="hq-invoice-new-submit"
          className="px-6 py-3 rounded-full bg-black text-white font-black text-[12px] tracking-[0.2em] uppercase disabled:opacity-40 disabled:cursor-not-allowed">
          {submitting ? "Creating…" : "Save as draft"}
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------- Detail

export function HQInvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await hqApi.get(`/api/hq/invoices/${id}`);
      setInv(r.data);
    } catch { toast.error("Failed to load invoice"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doInvoiceAction = async (path, label) => {
    setBusy(path);
    try {
      const r = await hqApi.post(`/api/hq/invoices/${id}${path}`);
      setInv(r.data);
      toast.success(label);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  const doInstallmentAction = async (iid, action, label) => {
    setBusy(`${iid}:${action}`);
    try {
      const r = await hqApi.post(`/api/hq/invoices/installments/${iid}/${action}`);
      if (r.data.public_url) {
        try { await navigator.clipboard.writeText(r.data.public_url); toast.success(`${label} · link copied`); }
        catch { toast.success(`${label} · ${r.data.public_url}`); }
      } else { toast.success(label); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  if (loading || !inv) return <div className="text-black/50 text-sm">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto" data-testid="hq-invoice-detail-page">
      <Link to="/hq/invoices" className="text-[11px] tracking-[0.2em] uppercase font-bold text-black/60 hover:text-black">← Back</Link>
      <div className="mt-3 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold tracking-[0.24em] uppercase text-black/50">Invoice</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-[-0.02em]" data-testid="hq-invoice-detail-number">{inv.invoice_number}</h1>
          {inv.description && <p className="mt-2 text-sm text-black/70 max-w-2xl">{inv.description}</p>}
        </div>
        <StatusPill status={inv.status} />
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 border-2 border-black rounded-2xl p-5 bg-white">
        <div><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Total</div><div className="mt-1 font-black text-2xl">{fmt(inv.total_amount_minor, inv.currency)}</div></div>
        <div><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Paid</div><div className="mt-1 font-black text-2xl text-[#0E5A17]">{fmt(inv.amount_paid_minor, inv.currency)}</div></div>
        <div><div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Outstanding</div><div className="mt-1 font-black text-2xl text-[#8A4A00]">{fmt(inv.amount_outstanding_minor, inv.currency)}</div></div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {inv.status === "DRAFT" && <button onClick={() => doInvoiceAction("/ready", "Marked ready")} disabled={busy} data-testid="hq-invoice-mark-ready" className="px-4 py-2 rounded-full bg-black text-white text-[11px] font-bold tracking-[0.16em] uppercase">Mark ready</button>}
        {!["CANCELLED", "PAID"].includes(inv.status) && <button onClick={() => doInvoiceAction("/cancel", "Invoice cancelled")} disabled={busy} data-testid="hq-invoice-cancel" className="px-4 py-2 rounded-full border-2 border-[#EAEAEA] text-[11px] font-bold tracking-[0.16em] uppercase">Cancel invoice</button>}
      </div>

      <div className="mt-6 border-2 border-black rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-[#FFF9EC]">
            <tr className="text-left [&_th]:px-4 [&_th]:py-3 [&_th]:font-bold [&_th]:text-[10px] [&_th]:tracking-[0.2em] [&_th]:uppercase">
              <th>#</th><th>Milestone</th><th>Amount</th><th>Due</th><th>Provider</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inv.installments.map((inst) => (
              <tr key={inst.id} className="border-t border-[#EAEAEA]" data-testid={`hq-installment-row-${inst.sequence}`}>
                <td className="px-4 py-3">{inst.sequence}</td>
                <td className="px-4 py-3 font-bold">{inst.label}</td>
                <td className="px-4 py-3 font-black">{fmt(inst.amount_minor, inst.currency)}</td>
                <td className="px-4 py-3 text-black/60">{inst.due_date || "—"}</td>
                <td className="px-4 py-3 text-black/80 uppercase text-[11px] tracking-[0.14em]">{inst.provider}</td>
                <td className="px-4 py-3"><StatusPill status={inst.status} /></td>
                <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                  {!["PAID","CANCELLED"].includes(inst.status) && (
                    <>
                      <button onClick={() => doInstallmentAction(inst.id, "send", "Payment request sent")}
                              disabled={busy || !["READY", "DRAFT"].includes(inst.status)}
                              data-testid={`hq-installment-send-${inst.sequence}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-black text-white text-[10px] font-bold tracking-[0.14em] uppercase disabled:opacity-30">
                        <Send className="w-3 h-3" /> Send
                      </button>
                      <button onClick={() => doInstallmentAction(inst.id, "resend", "Resent · new link")}
                              disabled={busy || !["SENT","VIEWED","OVERDUE"].includes(inst.status)}
                              data-testid={`hq-installment-resend-${inst.sequence}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border-2 border-[#EAEAEA] text-[10px] font-bold tracking-[0.14em] uppercase disabled:opacity-30">
                        <RefreshCw className="w-3 h-3" /> Resend
                      </button>
                      <button onClick={() => doInstallmentAction(inst.id, "cancel", "Payment link cancelled")}
                              disabled={busy}
                              data-testid={`hq-installment-cancel-${inst.sequence}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border-2 border-[#EAEAEA] text-[10px] font-bold tracking-[0.14em] uppercase disabled:opacity-30">
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </>
                  )}
                  {inst.status === "PAID" && inst.paid_at && (
                    <span className="text-[11px] text-[#0E5A17] font-bold">Paid {new Date(inst.paid_at).toLocaleDateString()}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
